import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import api from '@/utils/api';
import ScrollReveal from '@/components/effects/ScrollReveal';
import { Pen, Tag } from 'lucide-react';

export default function Journal() {
  const { t, localizedField } = useLang();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [activeTag, setActiveTag] = useState(null);
  const [allTags, setAllTags] = useState([]);

  useEffect(() => {
    const params = activeTag ? `?tag=${activeTag}` : '';
    api.get(`/journal${params}`).then(r => {
      setPosts(r.data.posts || []);
      setTotal(r.data.total || 0);
      const tags = new Set();
      (r.data.posts || []).forEach(p => (p.tags || []).forEach(tag => tags.add(tag)));
      if (!activeTag) setAllTags([...tags]);
    }).catch(() => {});
  }, [activeTag]);

  return (
    <div data-testid="journal-page" className="min-h-screen py-20 md:py-32 px-4">
      <div className="max-w-6xl mx-auto">
        <ScrollReveal>
          <h1 className="text-4xl sm:text-5xl font-display text-heading mb-4" data-testid="journal-title">{t('journal.title')}</h1>
          <p className="text-text-muted font-body mb-10">Thoughts, reflections, and explorations</p>
        </ScrollReveal>

        {allTags.length > 0 && (
          <ScrollReveal>
            <div className="flex flex-wrap gap-2 mb-10">
              <button
                onClick={() => setActiveTag(null)}
                className={`px-4 py-1.5 rounded-full text-sm font-body transition-all ${!activeTag ? 'gold-button' : 'glass-button'}`}
                data-testid="tag-all"
              >
                {t('journal.allPosts')}
              </button>
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(tag)}
                  className={`px-4 py-1.5 rounded-full text-sm font-body transition-all flex items-center gap-1 ${activeTag === tag ? 'gold-button' : 'glass-button'}`}
                >
                  <Tag size={12} /> {tag}
                </button>
              ))}
            </div>
          </ScrollReveal>
        )}

        {posts.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center">
            <Pen size={48} className="text-sacred-gold/30 mx-auto mb-4" />
            <p className="text-text-muted font-body">{t('journal.noPost')}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post, i) => (
              <ScrollReveal key={post.id} delay={i * 0.08}>
                <Link to={`/journal/${post.slug}`} className="glass-card rounded-2xl overflow-hidden block group h-full" data-testid={`journal-card-${post.slug}`}>
                  {post.header_image_url ? (
                    <div className="aspect-video overflow-hidden">
                      <img src={post.header_image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${post.header_image_url}` : post.header_image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  ) : (
                    <div className="aspect-video bg-warm-black flex items-center justify-center">
                      <Pen size={32} className="text-sacred-gold/20" />
                    </div>
                  )}
                  <div className="p-5 space-y-3">
                    <h3 className="text-lg font-display text-heading group-hover:text-sacred-gold transition-colors">{localizedField(post.title)}</h3>
                    {post.excerpt && <p className="text-text-muted text-sm font-body line-clamp-2">{localizedField(post.excerpt)}</p>}
                    <div className="flex items-center gap-3 text-text-muted/50 text-xs font-body">
                      {post.published_at && <span>{new Date(post.published_at).toLocaleDateString()}</span>}
                      {post.tags?.length > 0 && <span className="flex items-center gap-1"><Tag size={10} /> {post.tags[0]}</span>}
                    </div>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
